import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  getAccountsForEmployer,
  getEmployers,
} from "../../utils/employersCatalog";

const inputCls =
  "w-full rounded-xl border border-[#d4cceb] bg-white px-4 py-3 text-[0.9375rem] text-brand-dark placeholder:text-brand-gray/50 focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/15 transition-colors";

export default function EmployerAccountSelects({ value, onChange, disabled }) {
  const { employerId = "", accountId = "" } = value || {};
  const employers = getEmployers();
  const accounts = getAccountsForEmployer(employerId);
  const selectedEmployer = employers.find((e) => e.id === employerId) || null;

  const [query, setQuery] = useState(selectedEmployer?.name || "");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Sync input text with selected employer when dropdown closes
  useEffect(() => {
    if (!open) setQuery(selectedEmployer?.name || "");
  }, [open, selectedEmployer]);

  // Close on outside click
  useEffect(() => {
    function onOutside(e) {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll("[role='option']");
      items[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || selectedEmployer?.name.toLowerCase() === q) return employers;
    return employers.filter((e) => e.name.toLowerCase().includes(q));
  }, [employers, query, selectedEmployer]);

  function handleSelect(em) {
    const next = getAccountsForEmployer(em.id);
    onChange({
      employerId: em.id,
      accountId: next.length === 1 ? next[0].id : "",
    });
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleInputChange(e) {
    setQuery(e.target.value);
    // Clear selection if user types something different
    if (selectedEmployer && e.target.value !== selectedEmployer.name) {
      onChange({ employerId: "", accountId: "" });
    }
    setOpen(true);
    setActiveIndex(-1);
  }

  function handleKeyDown(e) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        setActiveIndex(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIndex((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && filtered[activeIndex]) {
        handleSelect(filtered[activeIndex]);
        e.preventDefault();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* ── Employer combobox ── */}
      <div className="space-y-1.5">
        <label htmlFor="employer-input" className="portal-label">
          Empresa
        </label>
        <div ref={containerRef} className="relative">
          <input
            id="employer-input"
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            value={query}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder="Busca tu empresa…"
            autoComplete="off"
            className={`${inputCls} pr-10`}
          />
          <ChevronDownIcon
            className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray pointer-events-none transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />

          {open && (
            <ul
              ref={listRef}
              role="listbox"
              aria-label="Empresas"
              className="absolute z-50 mt-1.5 w-full max-h-60 overflow-y-auto rounded-xl border border-[#d4cceb] bg-white py-1 shadow-xl shadow-black/10"
            >
              {filtered.length === 0 ? (
                <li className="px-4 py-3 text-sm text-brand-gray">
                  Sin resultados. Revisa el nombre.
                </li>
              ) : (
                filtered.map((em, i) => (
                  <li
                    key={em.id}
                    role="option"
                    aria-selected={em.id === employerId}
                    onMouseDown={() => handleSelect(em)}
                    className={[
                      "flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-[0.9rem]",
                      i === activeIndex
                        ? "bg-brand-beige text-brand-navy"
                        : "text-brand-dark hover:bg-brand-beige/70",
                      em.id === employerId ? "font-semibold" : "",
                    ].join(" ")}
                  >
                    <span>{em.name}</span>
                    {em.id === employerId && (
                      <CheckIcon className="h-4 w-4 shrink-0 text-brand-navy" aria-hidden />
                    )}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </div>

      {/* ── Account select (only when employer has multiple accounts) ── */}
      {employerId && accounts.length > 1 && (
        <div className="space-y-1.5">
          <label htmlFor="account-select" className="portal-label">
            Subempresa o cuenta
          </label>
          <select
            id="account-select"
            value={accountId}
            onChange={(e) => onChange({ employerId, accountId: e.target.value })}
            disabled={disabled}
            required
            className={`${inputCls} cursor-pointer appearance-none`}
          >
            <option value="">Selecciona la ubicación o división</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-brand-gray leading-relaxed">
            Elige la planta, ciudad o división que corresponda a tu contrato.
          </p>
        </div>
      )}

      {/* Auto-selected single account confirmation */}
      {employerId && accounts.length === 1 && (
        <p className="text-xs text-brand-gray -mt-1">
          Cuenta:{" "}
          <span className="font-semibold text-brand-dark">{accounts[0].name}</span>
        </p>
      )}
    </div>
  );
}
